<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :disabled="isSendingPacket"
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      Ping Test
    </template>

    <template #content>
      <div class="status-container">
        <div class="status-content">
          <p>
            Sent Packets:
            <span>{{ sentPackets || '' }}</span>
          </p>
          <p>
            Lost Packets:
            <span>
              {{ lostPackets || 0 }}
              {{ (lostPacketPercent && `(${lostPacketPercent}%)`) || '' }}
            </span>
          </p>
          <p>
            Status:&nbsp;
            <span class="status">
              {{ pingStatus }}
            </span>
          </p>
        </div>
      </div>

      <vue-apex-charts
        :options="chartOptions"
        :height="400"
        :series="series"
      />
    </template>

    <template #submit-button>
      Start Ping Test
    </template>

    <template #reset-button>
      {{ isSendingPacket ? 'Stop' : 'Reset' }}
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, computed } from 'vue';
import ping from 'web-pingjs';
import VueApexCharts from 'vue3-apexcharts';

import util from '@/Utils/util';
import type { ApexOptions } from 'apexcharts';

const pageId = 'ping-test';
const chartOptions: ApexOptions = {
  chart: {
    width: '100%',
    type: 'line',
  },
  stroke: {
    width: 1,
  },
  xaxis: {
    title: {
      text: 'Count',
    },
    labels: {
      show: true,
      hideOverlappingLabels: true,
    },
  },
  yaxis: {
    title: {
      text: 'Response Time (ms)',
    },
  },
};

const isSendingPacket = ref(false);
const sentPackets = ref(0);
const lostPackets = ref(0);
const series: Ref<any> = ref([]);

const lostPacketPercent = computed(() => {
  if (sentPackets.value === 0) {
    return 0;
  }

  const percent = (lostPackets.value * 100) / sentPackets.value;
  return percent.toFixed(2);
});

const pingStatus = computed(() => {
  if (lostPacketPercent.value === 0) {
    return '';
  }

  if (Number(lostPacketPercent.value) > 20) {
    return 'Poor';
  }

  if (Number(lostPacketPercent.value) > 10) {
    return 'Not Bad';
  }

  return 'Good';
});

const submitForm = async () => {
  resetForm();

  isSendingPacket.value = true;
  const values: number[] = [];

  for (var i = 0; i < 300; i++) {
    if (!isSendingPacket.value) {
      break;
    }

    sentPackets.value++;

    try {
      const response = await ping('https://twilio.com/');
      values[i] = response;
    } catch (error) {
      lostPackets.value++;
      values[i] = 0;
    }

    series.value = [
      {
        name: 'Ping Time (ms)',
        data: values,
      },
    ];
  }
  isSendingPacket.value = false;
};

const resetForm = () => {
  if (!isSendingPacket.value) {
    sentPackets.value = 0;
    lostPackets.value = 0;
    series.value = [
      {
        name: 'Ping Time (ms)',
        data: [],
      },
    ];
  }
  isSendingPacket.value = false;
};

util.moveToTop();
</script>

<style lang="scss">
.ping-test {
  .status-container {
    display: flex;
    justify-content: flex-end;
  }
  .status-content {
    width: 185px;
    border: 1px dashed #eb5e27;
    padding: 10px;
    background: #eae0dc;
    p {
      color: #208080;
      margin-bottom: 5px;
      &:last-child {
        margin-bottom: 0;
      }
      span {
        color: #f00;
        &.status {
          font-weight: bold;
        }
      }
    }
  }
  .apexcharts-toolbar {
    display: none;
  }
}
</style>
